'use client'

import { type IChatMinData, useChatApi, useSession } from '@repo/sdk'
import { shipperAdminChatName, useChatInput, useInfiniteChatMessages } from '@repo/ui'
import { z } from 'zod'

import { ChatHeader } from './chat-header.tsx'
import { ChatInput } from './chat-input.tsx'
import { Chat, useSubscribeToChatChannel } from './chat.tsx'

interface IProps {
  hideLoadId?: boolean
  hideLocation?: boolean
  loadData: IChatMinData
}

export const ChatView = ({ hideLoadId = false, hideLocation = false, loadData }: IProps) => {
  const { data: auth } = useSession()
  const { getMessagesForLoad, markChatMessagesAsRead, sendMessage } = useChatApi()

  const chatId = loadData.chat.id
  const userId = z.coerce.number().optional().parse(auth?.user?.id)

  useSubscribeToChatChannel({
    channel: shipperAdminChatName(chatId),
    chatId,
    event: 'message.created',
    loadId: loadData.id,
  })

  const infiniteChatProps = useInfiniteChatMessages({
    chatId: loadData?.chat.id,
    fetch: async (page: number) => {
      if (loadData?.chat.id) {
        return getMessagesForLoad(loadData.chat.id, page)
      }
    },
    loadId: loadData?.id,
    userData: {
      ...auth?.user,
      id: userId,
    },
  })

  const chatInputProps = useChatInput({
    chatId: loadData.chat.id,
    isAdmin: true,
    loadId: loadData.id,
    sendMessage: async message => await sendMessage(loadData.id, message),
    userId,
  })

  return (
    <div className={'flex h-full flex-col gap-4 pt-4 shadow-lg'}>
      <ChatHeader data={loadData} hideLoadId={hideLoadId} hideLocation={hideLocation} />
      <Chat
        {...infiniteChatProps}
        markMessagesAsRead={async () => {
          if (loadData?.chat.id) await markChatMessagesAsRead(loadData.chat.id)
        }}
      />

      <ChatInput {...chatInputProps} />
    </div>
  )
}
